import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatINR, formatPoints } from "../lib/format";
import type { Person, PooledCurrency, SpendByPerson } from "../lib/types";
import { Panel, Spinner } from "./ui";

/**
 * The household's points, per programme.
 *
 * The pooled figure is what the family can actually spend; the split beneath
 * it is what says whose card a transfer would have to come off, which is the
 * part that decides whether a redemption is possible at all.
 */
export function HouseholdPool() {
  const [pooled, setPooled] = useState<PooledCurrency[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [spend, setSpend] = useState<SpendByPerson[]>([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{
        pooled: PooledCurrency[];
        people: Person[];
        spendByPerson: SpendByPerson[];
      }>("/people/pool");
      setPooled(r.pooled);
      setPeople(r.people);
      setSpend(r.spendByPerson.filter((row) => row.personId));
    } catch {
      setPooled([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (pooled === null) return <Panel title="Pooled across the household"><Spinner /></Panel>;

  // With nobody but yourself, the split says nothing the balance does not.
  const worthShowing = pooled.some((c) => c.byPerson.length > 1) || people.length > 1;
  if (!worthShowing || pooled.length === 0) return null;

  const totalValue = pooled.reduce((sum, c) => sum + c.totalValue, 0);

  return (
    <Panel
      title="Pooled across the household"
      subtitle="What the family holds in each programme. Points belong to each card's primary holder, add-on spending included."
      actions={<span className="text-sm font-semibold tabular-nums text-ink-900">{formatINR(totalValue)}</span>}
    >
      <ul className="space-y-4">
        {pooled.map((currency) => {
          const largest = currency.byPerson[0]?.points ?? 0;
          return (
            <li key={currency.currencyId ?? currency.currencyName}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-ink-900">{currency.currencyName}</span>
                <span className="text-sm tabular-nums text-ink-600">
                  {formatPoints(currency.totalPoints)} pts · {formatINR(currency.totalValue)}
                </span>
              </div>
              <ul className="mt-1.5 space-y-1">
                {currency.byPerson.map((person) => (
                  <li key={person.personId ?? person.personName} className="flex items-center gap-3 text-sm">
                    <span className="w-28 shrink-0 truncate text-ink-600" title={person.personName}>
                      {person.personName}
                    </span>
                    {/* A bar only says something when there is another share to
                        compare it against. */}
                    {currency.byPerson.length > 1 && (
                      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-ink-100">
                        <span
                          className="block h-full rounded-full bg-accent-500"
                          style={{ width: `${largest ? Math.max(2, (person.points / largest) * 100) : 0}%` }}
                        />
                      </span>
                    )}
                    <span
                      className={`shrink-0 tabular-nums text-ink-700 ${
                        currency.byPerson.length > 1 ? "w-24 text-right" : ""
                      }`}
                    >
                      {formatPoints(person.points)}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>

      {spend.length > 0 && (
        <div className="mt-6 border-t border-ink-200/70 pt-4">
          <h3 className="label mb-1">Who spent what, last 12 months</h3>
          <p className="mb-3 text-xs text-ink-500">
            An add-on cardholder&apos;s spending earns into the primary&apos;s account, so this is where
            the money went rather than whose points it became.
          </p>
          <ul className="space-y-1.5">
            {spend.map((row) => (
              <li key={row.personId} className="flex items-center gap-3 text-sm">
                <span className="w-28 shrink-0 truncate text-ink-600" title={row.personName}>
                  {row.personName}
                </span>
                <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-ink-100">
                  <span
                    className="block h-full rounded-full bg-accent-500"
                    style={{ width: `${spend[0].amount ? Math.max(2, (row.amount / spend[0].amount) * 100) : 0}%` }}
                  />
                </span>
                <span className="w-28 shrink-0 text-right tabular-nums text-ink-700">
                  {formatINR(row.amount)}
                </span>
                <span className="w-24 shrink-0 text-right text-xs text-ink-500">
                  {formatPoints(row.points)} pts
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}
